/**
 * The picture a sent turn's pins were drawn on, frozen.
 *
 * A turn's annotation block says WHERE the notes were (`withAnnotations` writes positions in artifact px)
 * but not WHAT the artifact looked like, and the thread resolved the picture live —
 * `thumbSrcFor(slideId, <current bake>)`. So editing the artifact moved the ground out from under the
 * pins: "2 is stale" ends up pointing at a headline that no longer exists, on a render the user never saw
 * (Beka, 2026-09-17). The blob keeps exactly one previous source (`docPrevSource`, for a single undo
 * step), so an old render cannot be rebuilt after the fact. It has to be captured at send time.
 *
 * WHERE THE PICTURE LIVES. The send puts it in its own multipart part, `referenceImages`, and the backend
 * stores it on the message row and returns it as `message.referenceImages` — separate from `images`, which
 * is both what the user attached and what the model is shown. So the thread gets its picture back on every
 * reload, the bubble does not draw it twice, and the agent's input is exactly what it was before any of
 * this existed: the `[Pointed annotations…]` block, in words.
 *
 * (It rode `images` for a day, recognised again by the content hash the backend files it under. That
 * worked and cost the agent an image per annotated turn. `reference_image_urls` replaced it — see
 * `ChatMessage` in the backend — and the hash matching, the blob's ground list and the canvas passthrough
 * all went with it.)
 *
 * WITHIN THE SESSION there is no URL yet: the send is still in flight and the optimistic turn holds only
 * the local file. So the sender also parks the picture against the optimistic message id, which is what
 * makes the ground stop moving the moment you send rather than on the next reload.
 */

/**
 * Render width of a frozen ground. The reference draws 190px wide, so this is retina with room to zoom,
 * and a JPEG of it stays in the tens of KB — the same order as the screenshots users attach by hand.
 */
export const GROUND_W = 960;

/**
 * The store, on the `artifactThumbs` pattern and for the same reason: the reader is a render body four
 * components below the send, and threading one `<img src>` through all of them would put the machinery in
 * every signature on the way down.
 */
const listeners = new Set<() => void>();
let version = 0;
const sessionGrounds = new Map<string, string>();

export function subscribeAnnotationGrounds(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function annotationGroundsVersion(): number {
  return version;
}

/** The server renders no ground, so its snapshot is a constant. */
export function annotationGroundsServerVersion(): number {
  return 0;
}

/**
 * The local picture for a turn sent in this session, before the stored URL exists.
 *
 * Keyed by the OPTIMISTIC message id, which the server's copy of the row replaces a moment later — by
 * then `message.referenceImages` answers instead, so the entry is simply never read again.
 */
export function rememberSessionGround(messageId: string, src: string): void {
  if (!messageId || !src) return;
  sessionGrounds.set(messageId, src);
  version += 1;
  for (const listener of listeners) listener();
}

export function sessionGroundFor(messageId: string | undefined): string | undefined {
  return messageId ? sessionGrounds.get(messageId) : undefined;
}
